import { AxiosError } from 'axios';
import { getBlob, post } from '@/utils';
import { trackUploadedAssets } from '@/store/assets/helpers';
import { IUploadedFile } from './types';

// ==============================|| FILE UPLOAD - ACȚIUNI (api/assets) ||============================== //
// Doar `postFile` mai e folosit (avatare profil/utilizator, formularul de administrație).
// ATENȚIE: uploadul face AUTO-tracking în slice-ul de assets — după o salvare reușită
// care leagă assetul, apelează consumeTrackedAssetsByIds(...) (vezi memoria proiectului),
// altfel GC-ul de pe schimbarea de rută îl șterge din bucket.

const normalizeNumber = (value: unknown): number => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeUploadedFile = (payload: unknown): IUploadedFile => {
	const data = payload as Record<string, unknown>;
	const publicUrl = String(data?.publicUrl ?? data?.url ?? '');
	const fileName = String(data?.fileName ?? data?.filename ?? '');
	const originalName = String(data?.originalName ?? data?.originalname ?? fileName);
	const mimeType = String(data?.mimeType ?? data?.mimetype ?? '');
	const sizeBytes = normalizeNumber(data?.sizeBytes ?? data?.size);

	return {
		id: normalizeNumber(data?.id),
		fileName,
		originalName,
		mimeType,
		extension: String(data?.extension ?? ''),
		sizeBytes,
		publicUrl,
		type: String(data?.type ?? ''),
		width: normalizeNumber(data?.width),
		height: normalizeNumber(data?.height),
		pageCount: normalizeNumber(data?.pageCount),
		url: publicUrl,
		mimetype: mimeType,
		originalname: originalName,
		size: sizeBytes
	};
};

const extractUploadedFiles = (payload: unknown): IUploadedFile[] => {
	if (Array.isArray(payload)) {
		return payload.map(normalizeUploadedFile);
	}

	const data = payload as Record<string, unknown>;
	const candidates = [
		data?.items,
		data?.attachments,
		data?.files,
		data?.data,
		data?.file,
		data?.attachment
	];

	for (const candidate of candidates) {
		if (Array.isArray(candidate)) {
			return candidate.map(normalizeUploadedFile);
		}

		if (candidate && typeof candidate === 'object') {
			return [normalizeUploadedFile(candidate)];
		}
	}

	if (payload && typeof payload === 'object') {
		return [normalizeUploadedFile(payload)];
	}

	return [];
};

async function postFile(file: File): Promise<IUploadedFile> {
	try {
		const formData = new FormData();
		formData.append('files', file);

		const response = await post('api/assets', formData);
		const files = extractUploadedFiles(response.data);

		if (files.length === 0) {
			throw new Error('Nu am primit informațiile fișierului încărcat.');
		}

		trackUploadedAssets(files[0]);
		return files[0];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reusit sa incarcam fisierul.');
	}
}

// Numele fișierului din antetul Content-Disposition (attachment; filename[*]=...).
const filenameFromDisposition = (disposition: unknown): string | undefined => {
	if (typeof disposition !== 'string') return undefined;
	const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
	if (utf8?.[1]) {
		try {
			return decodeURIComponent(utf8[1]);
		} catch {
			/* nume malformat — cădem pe varianta simplă */
		}
	}
	const plain = disposition.match(/filename="?([^";]+)"?/i);
	return plain?.[1];
};

// GET api/assets/:id/download — descărcare SAME-ORIGIN (cu auth), fără problemele de CORS
// ale URL-urilor publice de bucket (acelea nu pot fi citite ca blob din browser). Salvăm
// blob-ul printr-un object URL; numele vine din Content-Disposition, cu fallback pe
// `preferredName` sau un nume generat. Aruncă la eșec — apelantul notifică.
async function downloadAsset(assetId: number, preferredName?: string): Promise<void> {
	try {
		const response = await getBlob(`api/assets/${assetId}/download`);
		const name =
			filenameFromDisposition(response.headers?.['content-disposition']) ||
			preferredName?.trim() ||
			`asset-${assetId}`;
		const objectUrl = URL.createObjectURL(response.data);
		const link = document.createElement('a');
		link.href = objectUrl;
		link.download = name;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(objectUrl);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error?: string };
		throw new Error(data?.error || 'Nu am reușit să descărcăm fișierul.');
	}
}

export { postFile, downloadAsset };
